import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@perch/ui-tokens';

const buttonVariants = cva(
  'inline-flex items-center justify-center font-medium rounded-full transition-[transform,opacity,box-shadow,background-color,border-color,color] duration-150 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97]',
  {
    variants: {
      variant: {
        coral: 'bg-[#FF6B35] hover:bg-[#E85D2C] text-white shadow-[0_2px_12px_rgba(255,107,53,0.2)] hover:shadow-[0_4px_20px_rgba(255,107,53,0.35)] hover:-translate-y-px disabled:bg-stone-200 dark:disabled:bg-stone-800 disabled:shadow-none',
        dark: 'bg-stone-900 dark:bg-stone-100 hover:bg-stone-800 dark:hover:bg-stone-200 text-white dark:text-stone-900 shadow-sm disabled:bg-stone-200 dark:disabled:bg-stone-800 disabled:text-stone-400 dark:disabled:text-stone-600',
        outline: 'bg-white dark:bg-stone-800 hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-900 dark:text-stone-100 border border-stone-200 dark:border-stone-700 hover:border-stone-300 dark:hover:border-stone-600',
        ghost: 'bg-transparent hover:bg-stone-50 dark:hover:bg-stone-800 text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100',
        danger: 'bg-transparent hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
        'danger-filled': 'bg-red-600 hover:bg-red-700 text-white disabled:bg-stone-200 dark:disabled:bg-stone-800 disabled:shadow-none',
        emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-stone-200 dark:disabled:bg-stone-800 disabled:shadow-none',
        amber: 'bg-amber-600 hover:bg-amber-700 text-white disabled:bg-stone-200 dark:disabled:bg-stone-800 disabled:shadow-none',
      },
      size: {
        default: 'px-6 py-2.5 text-sm',
        sm: 'px-3.5 py-1.5 text-xs',
        xs: 'px-2 py-0.5 text-xs',
        lg: 'px-8 py-3 text-base',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'coral',
      size: 'default',
      fullWidth: false,
    },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: ReactNode;
}

export function Button({
  children,
  variant,
  size,
  fullWidth,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...props}
    >
      {children}
    </button>
  );
}

export { buttonVariants };
